import { cn } from "@/lib/utils";
import {
  ReactFramework,
  TemplateStack,
  StackMintlifyLinkMapping,
} from "@/utils/templateProjects";
import { ArrowLeft, Copy } from "lucide-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import React, { useState, useEffect, useMemo } from "react";
import SupabaseConnect from "./LeftNav/SupabaseConnect";
import { toast } from "sonner";
import useSupabaseConnectStore from "@/store/zustand/supabaseConnectStore";
import Stripe from "stripe";
import { Polar } from "@polar-sh/sdk";
import { Button } from "@/components/ui/button";
export type FormValues = {
  clerk: {
    publishableKey: string;
    secretKey: string;
  };
  convex: {
    deploymentUrl: string;
    deploymentKey: string;
  };
  polar: {
    accessToken: string;
    organizationId: string;
    webhookSecret: string;
    verifyWebhookFields: string;
  };
  stripe: {
    secretKey: string;
    webhookSecret: string;
    publicKey: string;
    verifyWebhookFields: string;
  };
};

// ===== Type Definitions =====
interface ProviderSetupProps {
  desiredTemplateStack: TemplateStack;
  reactFramework: ReactFramework;
  setReactFramework: (framework: ReactFramework) => void;
  onBack: () => void;
  onComplete: (selectedTemplateStack: TemplateStack) => void;
  formValues: FormValues;
  onFormValuesChange?: (formValues: FormValues) => void;
}

interface CardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
  badge?: {
    text: string;
    color: string;
    position?: "title" | "corner";
  };
}

interface OptionCardProps {
  id: string | TemplateStack;
  title: string;
  description: string;
  icon: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
  badge?: {
    text: string;
    color: string;
    position?: "title" | "corner";
  };
}

// ===== Reusable Components =====
const Card: React.FC<CardProps> = ({
  title,
  description,
  icon,
  isSelected,
  onClick,
  disabled,
  badge,
}) => (
  <div className="flex flex-col h-[170px]">
    <div
      className={cn(
        "w-[300px] rounded-lg min-h-[140px] p-5 transition-all bg-gradient-to-b from-[#1F1F22] to-[#131519]",
        isSelected
          ? "border border-[#6F55F2]"
          : disabled
          ? "opacity-50 cursor-not-allowed border-0"
          : "hover:bg-gray-900 cursor-pointer border-0"
      )}
      onClick={disabled ? undefined : onClick}
    >
      <div className="flex flex-col gap-2 h-full">
        <div className="flex items-start w-full">
          <div className="flex flex-col w-full items-start gap-1.5">
            {icon}
            <div className="flex items-center justify-start gap-2">
              <span className="font-medium">{title}</span>
              {badge && badge.position === "title" && (
                <span className={`text-xs ${badge.color} px-2 py-0.5 rounded`}>
                  {badge.text}
                </span>
              )}
            </div>
          </div>
          {badge && (!badge.position || badge.position === "corner") && (
            <span
              className={`ml-auto text-xs ${badge.color} px-2 py-0.5 rounded`}
            >
              {badge.text}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-400">{description}</p>
      </div>
    </div>
    <p
      className={`text-xs text-gray-500 mt-1 italic text-center h-5 ${
        disabled ? "opacity-100" : "opacity-0"
      }`}
    >
      Not compatible with current selection
    </p>
  </div>
);

// Integration form component for reuse
const IntegrationForm: React.FC<{
  title: string;
  icon: string;
  description: string;
  docsUrl: string;
  fields: Array<{
    label: string;
    placeholder: string;
    type?: string;
    onChange?: (value: string) => void;
    validationFunction?: (value: string) => boolean;
    validationErrorMessage?: string | undefined;
    checkboxLabel?: React.ReactNode;
    value: string;
  }>;
  isInfoOnly?: boolean;
  infoMessage?: string | undefined;
  footer?: React.ReactNode;
  selectedTemplateStack: TemplateStack;
  reactFramework: ReactFramework;
}> = ({
  title,
  icon,
  description,
  docsUrl,
  fields,
  isInfoOnly,
  infoMessage,
  footer,
  selectedTemplateStack,
  reactFramework,
}) => {
  const mintlifyURL =
    StackMintlifyLinkMapping?.[reactFramework]?.[selectedTemplateStack];

  return (
    <div className="border border-[#2A2A2D] p-4 rounded-lg mb-4 relative">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <img src={icon} alt={title} className="w-5 h-5" />
          <h3 className="text-base font-medium">{title}</h3>
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="text-xs bg-secondary text-white border border-[#2A2A2D]"
          onClick={() => window.open(mintlifyURL || docsUrl, "_blank")}
        >
          Learn More
        </Button>
      </div>
      <p className="text-gray-400 mb-3 text-sm">{description}</p>

      {isInfoOnly ? (
        <div className="bg-[#1F1F22] p-3 rounded-md border border-[#2A2A2D]">
          <p className="text-sm text-gray-300">{infoMessage}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {fields.map((field, index) => (
            <div key={index}>
              <label className="block text-xs font-medium mb-1">
                {field.label}
              </label>
              {field.validationFunction &&
                field?.value &&
                !field.validationFunction(field?.value) && (
                  <p className="text-red-500 text-sm">
                    {field.validationErrorMessage}
                  </p>
                )}

              {field.type !== "checkbox" ? (
                <input
                  type={field.type || "text"}
                  placeholder={field.placeholder}
                  className="w-full bg-[#2A2A2D] border border-gray-700 rounded-md p-1.5 text-sm text-white"
                  value={field?.value || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange?.(value);
                  }}
                />
              ) : (
                <div className="flex w-full items-center justify-start gap-2">
                  <input
                    type="checkbox"
                    placeholder={field.placeholder}
                    className="bg-[#2A2A2D] border border-gray-700 rounded-md p-1.5 text-sm text-white cursor-pointer"
                    checked={field?.value === "true"}
                    onChange={(e) => {
                      const value = e.target.checked;
                      field.onChange?.(value ? "true" : "false");
                    }}
                  />
                  {field?.checkboxLabel ? (
                    field?.checkboxLabel
                  ) : (
                    <p className="text-sm text-gray-400">{field?.label}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {footer}
    </div>
  );
};

// ===== Main Component =====
const ProviderSetup: React.FC<ProviderSetupProps> = ({
  desiredTemplateStack,
  reactFramework,
  setReactFramework,
  onBack,
  onComplete,
  formValues,
  onFormValuesChange,
}) => {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [validationError, setValidationError] = useState<string | null>(null);

  // The selection inside the provider setup - only applies if "setup integrations" is clicked
  const [selectedTemplateStack, setSelectedTemplateStack] =
    useState<TemplateStack | null>(desiredTemplateStack);

  const connectedProjects = useSupabaseConnectStore(
    (state) => state.connectedProjects
  );

  const connectedProjectId = useSupabaseConnectStore(
    (state) => state.connectedProjectId
  );

  // Stack compatibility helpers
  const isClerkConvexStack = (stack: TemplateStack) => {
    return (
      stack === TemplateStack.CLERK_CONVEX_STRIPE ||
      stack === TemplateStack.CLERK_CONVEX_POLAR ||
      stack === TemplateStack.CLERK_CONVEX
    );
  };

  const isSupabaseStack = (stack: TemplateStack) => {
    return (
      stack === TemplateStack.SUPABASE_STRIPE ||
      stack === TemplateStack.SUPABASE_POLAR ||
      stack === TemplateStack.SUPABASE
    );
  };

  const hasStripePayment = (stack: TemplateStack) => {
    return (
      stack === TemplateStack.SUPABASE_STRIPE ||
      stack === TemplateStack.CLERK_CONVEX_STRIPE
    );
  };

  const hasPolarPayment = (stack: TemplateStack) => {
    return (
      stack === TemplateStack.SUPABASE_POLAR ||
      stack === TemplateStack.CLERK_CONVEX_POLAR
    );
  };

  const noPaymentInputStack =
    reactFramework === ReactFramework.VITE &&
    // @ts-expect-error TS2345
    isClerkConvexStack(selectedTemplateStack);

  // Add state to track integration setup completion
  const [supabaseConnected, setSupabaseConnected] = useState<boolean>(false);

  const [numStripePlans, setNumStripePlans] = useState<number>(0);
  const [isLoadingStripePlans, setIsLoadingStripePlans] =
    useState<boolean>(false);
  const [stripePlansRefetchTrigger, setStripePlansRefetchTrigger] =
    useState<number>(0);

  useEffect(() => {
    if (noPaymentInputStack) {
      return;
    }

    if (
      !formValues.stripe?.secretKey ||
      // @ts-expect-error TS2345
      !hasStripePayment(selectedTemplateStack)
    ) {
      setNumStripePlans(0);
      return;
    }

    const checkStripePlans = async () => {
      setIsLoadingStripePlans(true);
      const stripe = new Stripe(formValues.stripe.secretKey);
      stripe.plans
        .list()
        .then((plans) => {
          setNumStripePlans(plans.data.length);
          setIsLoadingStripePlans(false);
        })
        .catch((e) => {
          setIsLoadingStripePlans(false);
          console.error(e);
        });
    };

    checkStripePlans();
  }, [
    selectedTemplateStack,
    formValues.stripe?.secretKey,
    stripePlansRefetchTrigger,
  ]);

  // Do the same thing for Polar
  const [numPolarProducts, setNumPolarProducts] = useState<number>(0);
  const [isLoadingPolarProducts, setIsLoadingPolarProducts] =
    useState<boolean>(false);
  const [polarProductsRefetchTrigger, setPolarProductsRefetchTrigger] =
    useState<number>(0);

  useEffect(() => {
    if (noPaymentInputStack) {
      return;
    }

    if (
      !formValues.polar?.accessToken ||
      !formValues.polar?.organizationId ||
      // @ts-expect-error TS2345
      !hasPolarPayment(selectedTemplateStack)
    ) {
      setNumPolarProducts(0);
      return;
    }

    const checkPolarProducts = async () => {
      setIsLoadingPolarProducts(true);
      const polar = new Polar({
        accessToken: formValues.polar.accessToken,
        server: "sandbox",
      });
      try {
        const { result } = await polar.products.list({
          organizationId: formValues.polar.organizationId,
          isArchived: false,
        });

        setNumPolarProducts(result?.items?.length || 0);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingPolarProducts(false);
      }
    };

    checkPolarProducts();
  }, [
    selectedTemplateStack,
    formValues.polar?.accessToken,
    formValues.polar?.organizationId,
    polarProductsRefetchTrigger,
  ]);

  // Add a function to check for validation errors in form fields
  const hasValidationErrors = (): boolean => {
    // Check Clerk validation
    if (
      // @ts-expect-error TS2345
      isClerkConvexStack(selectedTemplateStack) &&
      formValues.clerk?.publishableKey &&
      !isValidClerkPublishableKey(formValues.clerk.publishableKey)
    ) {
      return true;
    }

    // Check Convex validation
    // @ts-expect-error TS2345
    if (isClerkConvexStack(selectedTemplateStack)) {
      if (
        formValues.convex?.deploymentUrl &&
        !isValidConvexDeploymentUrl(formValues.convex.deploymentUrl)
      ) {
        return true;
      }

      if (
        formValues.convex?.deploymentKey &&
        !isValidConvexDevKey(formValues.convex.deploymentKey)
      ) {
        return true;
      }
    }

    if (
      // @ts-expect-error TS2345
      hasPolarPayment(selectedTemplateStack) &&
      !isClerkConvexStack(selectedTemplateStack)
    ) {
      if (
        formValues.polar?.accessToken &&
        !isValidPolarAccessToken(formValues.polar.accessToken)
      ) {
        return true;
      }

      if (
        formValues.polar?.organizationId &&
        !isValidPolarOrganizationId(formValues.polar.organizationId)
      ) {
        return true;
      }
    }

    if (
      // @ts-expect-error TS2345
      hasStripePayment(selectedTemplateStack) &&
      !isClerkConvexStack(selectedTemplateStack)
    ) {
      if (
        formValues.stripe?.publicKey &&
        !isValidStripePublishableKey(formValues.stripe.publicKey)
      ) {
        return true;
      }

      if (
        formValues.stripe?.secretKey &&
        !isValidStripeSecretKey(formValues.stripe.secretKey)
      ) {
        return true;
      }

      if (
        formValues.stripe?.webhookSecret &&
        !isValidStripeWebhookSecret(formValues.stripe.webhookSecret)
      ) {
        return true;
      }
    }

    return false;
  };

  // Handle form field changes
  const handleFormChange = (
    integration: string,
    field: string,
    value: string
  ) => {
    if (!onFormValuesChange) return;

    const updatedFormValues = {
      ...formValues,
      [integration]: {
        ...formValues[integration as keyof FormValues],
        [field]: value,
      },
    };

    onFormValuesChange(updatedFormValues);
  };

  // ===== Helper Functions =====

  // Selection validation helpers
  const isFrameworkSelected = () => reactFramework !== null;

  const isNonePaymentSelected = () => {
    // If selectedTemplateStack is null or BLANK, it means "None" is selected for both Auth & DB and Payments
    if (
      selectedTemplateStack === null ||
      selectedTemplateStack === TemplateStack.BLANK
    ) {
      return true;
    }

    const nonePaymentStacks = [
      TemplateStack.SUPABASE,
      TemplateStack.CLERK_CONVEX,
    ];
    return nonePaymentStacks.includes(selectedTemplateStack);
  };

  // Check if Clerk+Convex is compatible with the current framework
  const isClerkConvexDisabled = () => {
    return reactFramework === ReactFramework.NEXT_JS;
  };

  // Check if a payment option is compatible with the current framework and auth/db selection
  const isPaymentOptionDisabled = (paymentType: "stripe" | "polar") => {
    // If no framework is selected, all payment options should be enabled
    if (reactFramework === null) {
      return false;
    }

    // payments should be disabled if no auth/db is selected
    if (
      selectedTemplateStack === null ||
      selectedTemplateStack === TemplateStack.BLANK
    ) {
      return true;
    }

    // All payment options are compatible with Supabase
    if (isSupabaseStack(selectedTemplateStack)) {
      return false;
    }

    // For Clerk+Convex, check framework compatibility
    if (isClerkConvexStack(selectedTemplateStack)) {
      return reactFramework !== ReactFramework.VITE;
    }

    return false;
  };

  // ===== Event Handlers =====

  // Validation and step navigation
  const handleProceedToSetup = () => {
    setValidationError(null);

    if (!isFrameworkSelected()) {
      setValidationError("Please select a framework before proceeding.");
      return;
    }

    // Check if both Auth & DB and Payments are set to "None" (BLANK)
    if (selectedTemplateStack === TemplateStack.BLANK) {
      // Set to null to represent framework-only selection
      setSelectedTemplateStack(null);
    }

    setCurrentStep(2);
  };

  // Framework selection logic
  const onFrameworkChange = (
    framework: ReactFramework.VITE | ReactFramework.NEXT_JS
  ) => {
    // Adjust stack selection based on framework change
    if (framework === ReactFramework.VITE) {
      // Keep existing selection if compatible
      if (
        selectedTemplateStack === TemplateStack.CLERK_CONVEX_STRIPE ||
        selectedTemplateStack === TemplateStack.CLERK_CONVEX_POLAR
      ) {
        // No need to reset, as Clerk+Convex is compatible with Vite
      }
      // Keep Supabase payment options intact when switching to Vite
    } else if (framework === ReactFramework.NEXT_JS) {
      // Reset incompatible selections
      // @ts-expect-error TS2345
      if (isClerkConvexStack(selectedTemplateStack)) {
        // Reset to blank when switching to Next.js with Clerk+Convex
        setSelectedTemplateStack(TemplateStack.BLANK);
      }
    }

    setReactFramework(framework);
  };

  // Stack selection logic
  const handleStackSelection = (stack: TemplateStack) => {
    // Special case for null (framework only)
    if (stack === null) {
      setSelectedTemplateStack(null);
      return;
    }

    setSelectedTemplateStack(stack);
  };

  // ===== Option Data =====

  // Framework options
  const frameworkOptions: OptionCardProps[] = [
    {
      id: ReactFramework.VITE,
      title: "Vite",
      description: "Build simple websites and web apps much faster.",
      icon: <img src="/images/vitejs.svg" alt="Vite" className="w-6 h-6" />,
      badge: {
        text: "Recommended",
        color: "bg-blue-600",
        position: "corner" as "corner",
      },
      onClick: () => onFrameworkChange(ReactFramework.VITE),
      isSelected: reactFramework === ReactFramework.VITE,
    },
    {
      id: ReactFramework.NEXT_JS,
      title: "Next.js",
      description:
        "Build faster, more powerful websites and web apps with less effort.",
      icon: (
        <img
          src="/images/nextjs_icon_dark.svg"
          alt="Next.js"
          className="w-6 h-6"
        />
      ),
      badge: {
        text: "BETA",
        color: "bg-blue-600",
        position: "title" as "title",
      },
      onClick: () => onFrameworkChange(ReactFramework.NEXT_JS),
      isSelected: reactFramework === ReactFramework.NEXT_JS,
    },
  ];

  // Auth & Database options
  const authDatabaseOptions: OptionCardProps[] = [
    {
      id: "none",
      title: "None",
      description:
        "Keep things simple - best for rapid prototyping and ideation.",
      icon: (
        <div className="w-6 h-6 flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-400">
            <path
              fill="currentColor"
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-4.42 3.58-8 8-8 4.42 0 8 3.58 8 8 0 4.42-3.58 8-8 8zm4-9h-3V8c0-.55-.45-1-1-1s-1 .45-1 1v3H8c-.55 0-1 .45-1 1s.45 1 1 1h3v3c0 .55.45 1 1 1s1-.45 1-1v-3h3c.55 0 1-.45 1-1s-.45-1-1-1z"
            />
          </svg>
        </div>
      ),
      onClick: () => handleStackSelection(TemplateStack.BLANK),
      isSelected:
        selectedTemplateStack === null ||
        selectedTemplateStack === TemplateStack.BLANK,
    },
    {
      id: "supabase",
      title: "Supabase",
      description:
        "An all in one database, authentication, and storage solution.",
      icon: (
        <img
          src="/images/supabase-logo-icon.svg"
          alt="Supabase"
          className="w-6 h-6"
        />
      ),
      badge: {
        text: "BETA",
        color: "bg-blue-600",
        position: "title" as "title",
      },
      onClick: () => {
        // Handle Supabase selection with appropriate payment option
        if (selectedTemplateStack === TemplateStack.CLERK_CONVEX_STRIPE) {
          handleStackSelection(TemplateStack.SUPABASE_STRIPE);
        } else if (selectedTemplateStack === TemplateStack.CLERK_CONVEX_POLAR) {
          handleStackSelection(TemplateStack.SUPABASE_POLAR);
        } else {
          handleStackSelection(TemplateStack.SUPABASE);
        }
      },
      // @ts-expect-error TS2345
      isSelected: isSupabaseStack(selectedTemplateStack),
    },
    {
      id: "clerk-convex",
      title: "Clerk + Convex",
      description:
        "The perfect backend for your React app with auth, database, and storage.",
      icon: (
        <div className="flex items-center gap-1">
          <img src="/images/clerk-auth.png" alt="Clerk" className="w-6 h-6" />
          <span className="text-xl">+</span>
          <img src="/images/convex.png" alt="Convex" className="w-6 h-6" />
        </div>
      ),
      badge: {
        text: "BETA",
        color: "bg-blue-600",
        position: "corner" as "corner",
      },
      disabled: isClerkConvexDisabled(),
      onClick: () => {
        // Handle Clerk+Convex selection with appropriate payment option
        if (selectedTemplateStack === TemplateStack.SUPABASE_STRIPE) {
          handleStackSelection(TemplateStack.CLERK_CONVEX_STRIPE);
        } else if (selectedTemplateStack === TemplateStack.SUPABASE_POLAR) {
          handleStackSelection(TemplateStack.CLERK_CONVEX_POLAR);
        } else {
          handleStackSelection(TemplateStack.CLERK_CONVEX);
        }
      },
      // @ts-expect-error TS2345
      isSelected: isClerkConvexStack(selectedTemplateStack),
    },
  ];

  // Payment options
  const paymentOptions: OptionCardProps[] = [
    {
      id: "none",
      title: "None",
      description:
        "No payment integration - best for simple apps and prototypes.",
      icon: (
        <div className="w-6 h-6 flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-400">
            <path
              fill="currentColor"
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-4.42 3.58-8 8-8 4.42 0 8 3.58 8 8 0 4.42-3.58 8-8 8zm4-9h-3V8c0-.55-.45-1-1-1s-1 .45-1 1v3H8c-.55 0-1 .45-1 1s.45 1 1 1h3v3c0 .55.45 1 1 1s1-.45 1-1v-3h3c.55 0 1-.45 1-1s-.45-1-1-1z"
            />
          </svg>
        </div>
      ),
      onClick: () => {
        // If Auth & DB is also "None", set to BLANK (will be converted to null on next step)
        if (selectedTemplateStack === null) {
          handleStackSelection(TemplateStack.BLANK);
        } else if (isSupabaseStack(selectedTemplateStack)) {
          handleStackSelection(TemplateStack.SUPABASE);
        } else if (isClerkConvexStack(selectedTemplateStack)) {
          handleStackSelection(TemplateStack.CLERK_CONVEX);
        } else {
          handleStackSelection(TemplateStack.BLANK);
        }
      },
      isSelected:
        selectedTemplateStack === null ||
        selectedTemplateStack === TemplateStack.BLANK ||
        isNonePaymentSelected(),
    },
    {
      id: "stripe",
      title: "Stripe",
      description: "Accept payments and manage subscriptions with Stripe.",
      icon: <img src="/images/stripe.svg" alt="Stripe" className="w-6 h-6" />,
      badge: {
        text: "BETA",
        color: "bg-blue-600",
        position: "title" as "title",
      },
      disabled: isPaymentOptionDisabled("stripe"),
      onClick: () => {
        // Handle Stripe selection based on current stack and framework
        if (selectedTemplateStack === TemplateStack.SUPABASE_POLAR) {
          handleStackSelection(TemplateStack.SUPABASE_STRIPE);
        } else if (
          reactFramework === ReactFramework.VITE &&
          selectedTemplateStack === TemplateStack.BLANK
        ) {
          handleStackSelection(TemplateStack.CLERK_CONVEX_STRIPE);
        } else if (reactFramework === ReactFramework.NEXT_JS) {
          handleStackSelection(TemplateStack.SUPABASE_STRIPE);
        } else if (
          reactFramework === ReactFramework.VITE &&
          selectedTemplateStack === TemplateStack.SUPABASE
        ) {
          // Allow Vite + Supabase + Stripe combination
          handleStackSelection(TemplateStack.SUPABASE_STRIPE);
        } else {
          handleStackSelection(TemplateStack.CLERK_CONVEX_STRIPE);
        }
      },
      // @ts-expect-error TS2345
      isSelected: hasStripePayment(selectedTemplateStack),
    },
    {
      id: "polar",
      title: "Polar",
      description: "Accept payments and manage subscriptions with Polar.",
      icon: (
        <img src="/images/polar-sh_dark.svg" alt="Polar" className="w-6 h-6" />
      ),
      badge: {
        text: "BETA",
        color: "bg-blue-600",
        position: "title" as "title",
      },
      disabled: isPaymentOptionDisabled("polar"),
      onClick: () => {
        // Handle Polar selection based on current stack and framework
        if (
          reactFramework === ReactFramework.VITE &&
          selectedTemplateStack === TemplateStack.BLANK
        ) {
          handleStackSelection(TemplateStack.CLERK_CONVEX_POLAR);
        } else if (
          selectedTemplateStack === TemplateStack.SUPABASE_STRIPE ||
          reactFramework === ReactFramework.NEXT_JS
        ) {
          handleStackSelection(TemplateStack.SUPABASE_POLAR);
        } else if (
          reactFramework === ReactFramework.VITE &&
          selectedTemplateStack === TemplateStack.SUPABASE
        ) {
          // Allow Vite + Supabase + Polar combination
          handleStackSelection(TemplateStack.SUPABASE_POLAR);
        } else if (
          selectedTemplateStack === TemplateStack.CLERK_CONVEX_STRIPE
        ) {
          handleStackSelection(TemplateStack.CLERK_CONVEX_POLAR);
        } else {
          handleStackSelection(TemplateStack.SUPABASE_POLAR);
        }
      },
      // @ts-expect-error TS2345
      isSelected: hasPolarPayment(selectedTemplateStack),
    },
  ];

  // ===== Validation Functions =====
  const isValidConvexDeploymentUrl = (value: string) => {
    const convexUrlRegex = /^https:\/\/[a-z0-9-]+\.convex\.cloud\/?$/;
    return convexUrlRegex.test(value);
  };

  const isValidConvexDevKey = (value: string) => {
    const deployKeyRegex = /^dev:[a-z0-9-]+\|[A-Za-z0-9+/]+={1,2}$/;
    return deployKeyRegex.test(value);
  };

  const isValidClerkPublishableKey = (value: string) => {
    const clerkPublishableKeyRegex = /^pk_test_[A-Za-z0-9_]+$/;
    return clerkPublishableKeyRegex.test(value);
  };

  const isValidPolarAccessToken = (value: string) => {
    const polarKeyRegex = /^polar_oat_[A-Za-z0-9]+$/;
    return polarKeyRegex.test(value);
  };

  const isValidPolarOrganizationId = (value: string) => {
    const simpleUUIDRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return simpleUUIDRegex.test(value);
  };

  const isValidStripePublishableKey = (value: string): boolean => {
    const stripePublishableKeyRegex = /^pk_(test|live)_[A-Za-z0-9]+$/;
    return stripePublishableKeyRegex.test(value);
  };

  const isValidStripeSecretKey = (value: string): boolean => {
    const stripeSecretKeyRegex = /^sk_(test|live)_[A-Za-z0-9]+$/;
    return stripeSecretKeyRegex.test(value);
  };

  const isValidStripeWebhookSecret = (value: string): boolean => {
    const stripeWebhookSecretRegex = /^whsec_[A-Za-z0-9]+$/;
    return stripeWebhookSecretRegex.test(value);
  };

  const connectedProject = useMemo(() => {
    return connectedProjects.find(
      (_project) => _project.id === connectedProjectId
    );
  }, [connectedProjectId, connectedProjects]);

  const supabasePaymentWebhookUrl = useMemo(() => {
    // @ts-expect-error TS2345
    return isSupabaseStack(selectedTemplateStack) &&
      supabaseConnected &&
      connectedProject
      ? `https://${connectedProject.id}.supabase.co/functions/v1/supabase-functions-payments-webhook`
      : undefined;
  }, [selectedTemplateStack, supabaseConnected, connectedProject]);

  // ===== Render Functions =====

  // Function to check if all required fields for the selected stack are filled
  const integrationComplete = useMemo((): boolean => {
    // Check for validation errors in form fields
    if (hasValidationErrors()) {
      return false;
    }

    if (
      selectedTemplateStack === null ||
      selectedTemplateStack === TemplateStack.BLANK
    ) {
      return true; // No integrations needed
    }

    // Check Supabase connection
    if (isSupabaseStack(selectedTemplateStack)) {
      if (!supabaseConnected) {
        return false;
      }
    }

    // Check Clerk+Convex
    if (isClerkConvexStack(selectedTemplateStack)) {
      const clerkComplete = !!(
        formValues.clerk?.publishableKey &&
        formValues.clerk?.secretKey === "true"
      );
      const convexComplete = !!(
        formValues.convex?.deploymentUrl && formValues.convex.deploymentKey
      );

      if (!clerkComplete || !convexComplete) {
        return false;
      }
    }

    if (hasStripePayment(selectedTemplateStack)) {
      const stripeComplete = noPaymentInputStack
        ? !!(
            formValues.stripe?.publicKey === "true" &&
            formValues.stripe?.secretKey === "true" &&
            formValues.stripe?.webhookSecret === "true" &&
            formValues.stripe?.verifyWebhookFields === "true"
          )
        : !!(
            formValues.stripe?.publicKey &&
            formValues.stripe?.secretKey &&
            formValues.stripe?.webhookSecret &&
            formValues.stripe?.verifyWebhookFields === "true" &&
            numStripePlans > 0
          );

      if (!stripeComplete) {
        return false;
      }
    }

    if (hasPolarPayment(selectedTemplateStack)) {
      const polarComplete = noPaymentInputStack
        ? !!(
            formValues.polar?.accessToken === "true" &&
            formValues.polar?.webhookSecret === "true" &&
            formValues.polar?.organizationId === "true" &&
            formValues.polar?.verifyWebhookFields === "true"
          )
        : !!(
            formValues.polar?.accessToken &&
            formValues.polar?.webhookSecret &&
            formValues.polar?.organizationId &&
            formValues.polar?.verifyWebhookFields === "true" &&
            numPolarProducts > 0
          );

      if (!polarComplete) {
        return false;
      }
    }

    return true;
  }, [
    formValues,
    selectedTemplateStack,
    supabaseConnected,
    numStripePlans,
    numPolarProducts,
  ]);

  // Step 2 - Integration setup
  const renderStep2 = () => {
    return (
      <div className="flex flex-col items-center justify-center w-full">
        <div className="w-full max-w-3xl mb-6 overflow-y-auto pr-2">
          {/* Show message when no integrations are selected */}
          {(selectedTemplateStack === null ||
            selectedTemplateStack === TemplateStack.BLANK) && (
            <div className="w-full max-w-3xl mb-4 border border-[#2A2A2D] p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-400">
                  <path
                    fill="currentColor"
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-4.42 3.58-8 8-8 4.42 0 8 3.58 8 8 0 4.42-3.58 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
                  />
                </svg>
                <h3 className="text-base font-medium">
                  No Integrations Selected
                </h3>
              </div>
              <p className="text-gray-400 text-sm">
                You've chosen to proceed without any integrations. Your project
                will be created with just the{" "}
                {reactFramework === ReactFramework.VITE ? "Vite" : "Next.js"}{" "}
                framework.
              </p>
            </div>
          )}

          {/* Supabase Integration */}
          {/*
           // @ts-expect-error TS2345 */}
          {isSupabaseStack(selectedTemplateStack) && (
            <div className="w-full max-w-3xl mb-4 border border-[#2A2A2D] p-4 rounded-lg">
              <SupabaseConnect
                forProjectCreate={true}
                onConnectionStatusChange={(connected) => {
                  setSupabaseConnected(connected);
                }}
              />
            </div>
          )}

          {/* Clerk + Convex Integration */}
          {/*
           // @ts-expect-error TS2345 */}
          {isClerkConvexStack(selectedTemplateStack) && (
            <>
              <IntegrationForm
                title="Clerk"
                icon="/images/clerk-auth.png"
                description="Enter your Clerk API keys for authentication."
                docsUrl="https://clerk.com/docs"
                fields={[
                  {
                    label: "Clerk Publishable Key",
                    placeholder: "pk_test_...",
                    onChange: (value) =>
                      handleFormChange("clerk", "publishableKey", value),
                    validationFunction: isValidClerkPublishableKey,
                    validationErrorMessage: "Invalid Clerk publishable key",
                    value: formValues.clerk?.publishableKey || "",
                  },
                  {
                    label: "Clerk Secret Key",
                    placeholder: "sk_test_...",
                    type: "checkbox",
                    onChange: (value) =>
                      handleFormChange("clerk", "secretKey", value),
                    checkboxLabel:
                      "Confirm that you have added your Clerk secret key in Convex.",
                    value: formValues.clerk?.secretKey || "false",
                  },
                ]}
                selectedTemplateStack={selectedTemplateStack}
                reactFramework={reactFramework}
              />

              <IntegrationForm
                title="Convex"
                icon="/images/convex.png"
                description="Enter your Convex deployment URL and development key."
                docsUrl="https://docs.convex.dev"
                fields={[
                  {
                    label: "Convex Deployment URL",
                    placeholder: "https://relaxed-puma-123.convex.cloud",
                    onChange: (value) =>
                      handleFormChange("convex", "deploymentUrl", value),
                    validationFunction: isValidConvexDeploymentUrl,
                    validationErrorMessage: "Invalid Convex deployment URL",
                    value: formValues.convex?.deploymentUrl || "",
                  },
                  {
                    label: "Convex Deploy Key",
                    placeholder: "dev_key_...",
                    type: "password",
                    onChange: (value) =>
                      handleFormChange("convex", "deploymentKey", value),
                    validationFunction: isValidConvexDevKey,
                    validationErrorMessage: "Invalid Convex deploy key",
                    value: formValues.convex?.deploymentKey || "",
                  },
                ]}
                selectedTemplateStack={selectedTemplateStack}
                reactFramework={reactFramework}
              />
            </>
          )}

          {/* Payment Providers */}
          {/*
           // @ts-expect-error TS2345 */}
          {hasStripePayment(selectedTemplateStack) && (
            <IntegrationForm
              title="Stripe"
              icon="/images/stripe.svg"
              description={
                reactFramework === ReactFramework.VITE &&
                selectedTemplateStack === TemplateStack.CLERK_CONVEX_STRIPE
                  ? "Your Stripe API keys must be stored in Convex."
                  : "Accept payments and manage subscriptions with Stripe."
              }
              docsUrl="https://stripe.com/docs"
              selectedTemplateStack={selectedTemplateStack}
              reactFramework={reactFramework}
              isInfoOnly={
                isSupabaseStack(selectedTemplateStack) && !supabaseConnected
              }
              infoMessage={
                reactFramework === ReactFramework.VITE &&
                selectedTemplateStack === TemplateStack.CLERK_CONVEX_STRIPE
                  ? `Your Stripe API keys will be securely stored in Convex when you deploy your application.`
                  : isSupabaseStack(selectedTemplateStack) && !supabaseConnected
                  ? "Connect Supabase to set up Stripe"
                  : undefined
              }
              fields={
                !(
                  reactFramework === ReactFramework.VITE &&
                  selectedTemplateStack === TemplateStack.CLERK_CONVEX_STRIPE
                )
                  ? [
                      {
                        label: "Stripe Publishable Key",
                        placeholder: "pk_test_...",
                        onChange: (value) =>
                          handleFormChange("stripe", "publicKey", value),
                        validationFunction: isValidStripePublishableKey,
                        validationErrorMessage:
                          "Invalid Stripe publishable key",
                        value: formValues.stripe?.publicKey || "",
                      },
                      {
                        label: "Stripe Secret Key",
                        placeholder: "sk_test_...",
                        type: "password",
                        onChange: (value) =>
                          handleFormChange("stripe", "secretKey", value),
                        validationFunction: isValidStripeSecretKey,
                        validationErrorMessage: "Invalid Stripe secret key",
                        value: formValues.stripe?.secretKey || "",
                      },
                      {
                        label: "Stripe Webhook Secret",
                        placeholder: "whsec_...",
                        type: "password",
                        onChange: (value) =>
                          handleFormChange("stripe", "webhookSecret", value),
                        validationFunction: isValidStripeWebhookSecret,
                        validationErrorMessage: "Invalid Stripe webhook secret",
                        value: formValues.stripe?.webhookSecret || "",
                      },
                      {
                        label: "Verify Webhook Fields",
                        type: "checkbox",
                        placeholder: "_",
                        onChange: (value) =>
                          handleFormChange(
                            "stripe",
                            "verifyWebhookFields",
                            value
                          ),
                        checkboxLabel: (
                          <p className="text-white text-sm">
                            I confirm that I have selected{" "}
                            <span className="underline">all events</span> for my
                            Stripe webhook.
                          </p>
                        ),
                        value: formValues.stripe?.verifyWebhookFields || "",
                      },
                    ]
                  : [
                      {
                        label: "Stripe Publishable Key",
                        placeholder: "pk_test_...",
                        onChange: (value) =>
                          handleFormChange("stripe", "publicKey", value),
                        type: "checkbox",
                        checkboxLabel: (
                          <p className="text-white text-sm">
                            I confirm that I have added my{" "}
                            <span className="underline">
                              Stripe publishable key
                            </span>{" "}
                            in Convex.
                          </p>
                        ),
                        value: formValues.stripe?.publicKey || "",
                      },
                      {
                        label: "Stripe Secret Key",
                        placeholder: "sk_test_...",
                        type: "checkbox",
                        onChange: (value) =>
                          handleFormChange("stripe", "secretKey", value),
                        checkboxLabel: (
                          <p className="text-white text-sm">
                            I confirm that I have added my{" "}
                            <span className="underline">Stripe secret key</span>{" "}
                            in Convex.
                          </p>
                        ),
                        value: formValues.stripe?.secretKey || "",
                      },
                      {
                        label: "Stripe Webhook Secret",
                        placeholder: "whsec_...",
                        type: "checkbox",
                        onChange: (value) =>
                          handleFormChange("stripe", "webhookSecret", value),
                        checkboxLabel: (
                          <p className="text-white text-sm">
                            I confirm that I have added my{" "}
                            <span className="underline">
                              Stripe webhook secret
                            </span>{" "}
                            in Convex.
                          </p>
                        ),
                        value: formValues.stripe?.webhookSecret || "",
                      },
                      {
                        label: "Verify Webhook Fields",
                        type: "checkbox",
                        placeholder: "_",
                        onChange: (value) =>
                          handleFormChange(
                            "stripe",
                            "verifyWebhookFields",
                            value
                          ),
                        checkboxLabel: (
                          <p className="text-white text-sm">
                            I confirm that I have selected{" "}
                            <span className="underline">all events</span> for my
                            Stripe webhook.
                          </p>
                        ),
                        value: formValues.stripe?.verifyWebhookFields || "",
                      },
                    ]
              }
              footer={
                <>
                  <div
                    className={cn(
                      "mt-3 bg-[#1F1F22] p-3 rounded-md border border-[#2A2A2D]",
                      !supabasePaymentWebhookUrl && "hidden"
                    )}
                  >
                    <p className="text-xs text-gray-300 mb-2">
                      Copy this URL for the Stripe webhook:
                    </p>
                    <div
                      className="flex items-center justify-between bg-[#2A2A2D] p-2 rounded cursor-pointer hover:bg-[#333338]"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          // @ts-expect-error TS2345
                          supabasePaymentWebhookUrl
                        );
                        toast.success("Copied to clipboard");
                      }}
                    >
                      <span className="text-xs text-white font-mono">
                        {supabasePaymentWebhookUrl}
                      </span>
                      <Copy className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div
                    className={cn(
                      "mt-3 bg-[#1F1F22] p-3 rounded-md border border-[#2A2A2D]",
                      (!formValues.stripe?.secretKey ||
                        noPaymentInputStack ||
                        (isSupabaseStack(selectedTemplateStack) &&
                          !supabaseConnected)) &&
                        "hidden"
                    )}
                  >
                    <div className="flex justify-between items-center">
                      <p
                        className={cn(
                          "text-xs text-gray-300",
                          numStripePlans === 0 && "text-red-500"
                        )}
                      >
                        {numStripePlans > 0
                          ? `You have ${numStripePlans} Stripe plan(s) set up in this project.`
                          : "You have no Stripe plans set up in this project."}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-6 px-2"
                        onClick={() => {
                          setStripePlansRefetchTrigger((prev) => prev + 1);
                          toast.success("Refreshed Stripe plans");
                        }}
                        disabled={isLoadingStripePlans}
                      >
                        {isLoadingStripePlans ? "Loading..." : "Refresh"}
                      </Button>
                    </div>
                  </div>
                </>
              }
            />
          )}

          {/*
           // @ts-expect-error TS2345 */}
          {hasPolarPayment(selectedTemplateStack) && (
            <IntegrationForm
              title="Polar"
              icon="/images/polar-sh_dark.svg"
              description={
                reactFramework === ReactFramework.VITE &&
                selectedTemplateStack === TemplateStack.CLERK_CONVEX_POLAR
                  ? "Your Polar API keys must be stored in Convex."
                  : "Accept payments and manage subscriptions with Polar."
              }
              docsUrl="https://polar.sh/docs"
              selectedTemplateStack={selectedTemplateStack}
              reactFramework={reactFramework}
              isInfoOnly={
                isSupabaseStack(selectedTemplateStack) && !supabaseConnected
              }
              infoMessage={
                reactFramework === ReactFramework.VITE &&
                selectedTemplateStack === TemplateStack.CLERK_CONVEX_POLAR
                  ? `Your Polar API keys will be securely stored in Convex when you deploy your application. You'll need to enter them later.`
                  : isSupabaseStack(selectedTemplateStack) && !supabaseConnected
                  ? "Connect Supabase to set up Polar"
                  : undefined
              }
              fields={
                !(
                  reactFramework === ReactFramework.VITE &&
                  selectedTemplateStack === TemplateStack.CLERK_CONVEX_POLAR
                )
                  ? [
                      {
                        label: "Polar Access Token",
                        placeholder: "polar_oat_...",
                        type: "password",
                        onChange: (value) =>
                          handleFormChange("polar", "accessToken", value),
                        validationFunction: isValidPolarAccessToken,
                        validationErrorMessage: "Invalid Polar access token",
                        value: formValues.polar?.accessToken || "",
                      },
                      {
                        label: "Polar Organization ID",
                        placeholder: "58045f0b-fa90-...",
                        onChange: (value) =>
                          handleFormChange("polar", "organizationId", value),
                        validationFunction: isValidPolarOrganizationId,
                        validationErrorMessage: "Invalid Polar organization ID",
                        value: formValues.polar?.organizationId || "",
                      },
                      {
                        label: "Polar Webhook Secret",
                        placeholder: "a1b2c3...",
                        type: "password",
                        onChange: (value) =>
                          handleFormChange("polar", "webhookSecret", value),
                        value: formValues.polar?.webhookSecret || "",
                      },
                      {
                        label: "Verify Webhook Fields",
                        type: "checkbox",
                        placeholder: "_",
                        onChange: (value) =>
                          handleFormChange(
                            "polar",
                            "verifyWebhookFields",
                            value
                          ),
                        checkboxLabel: (
                          <p className="text-white text-sm">
                            I confirm that I have selected{" "}
                            <span className="underline">all events</span> for my
                            Polar webhook.
                          </p>
                        ),
                        value: formValues.polar?.verifyWebhookFields || "",
                      },
                    ]
                  : [
                      {
                        label: "Polar Access Token",
                        placeholder: "polar_sk_...",
                        type: "checkbox",
                        onChange: (value) =>
                          handleFormChange("polar", "accessToken", value),
                        checkboxLabel: (
                          <p className="text-white text-sm">
                            I confirm that I have added my{" "}
                            <span className="underline">
                              Polar access token
                            </span>{" "}
                            in Convex.
                          </p>
                        ),
                        value: formValues.polar?.accessToken || "",
                      },
                      {
                        label: "Polar Organization ID",
                        placeholder: "polar_org_...",
                        type: "checkbox",
                        onChange: (value) =>
                          handleFormChange("polar", "organizationId", value),
                        checkboxLabel: (
                          <p className="text-white text-sm">
                            I confirm that I have added my{" "}
                            <span className="underline">
                              Polar organization ID
                            </span>{" "}
                            in Convex.
                          </p>
                        ),
                        value: formValues.polar?.organizationId || "",
                      },
                      {
                        label: "Polar Webhook Secret",
                        placeholder: "whsec_...",
                        type: "checkbox",
                        onChange: (value) =>
                          handleFormChange("polar", "webhookSecret", value),
                        checkboxLabel: (
                          <p className="text-white text-sm">
                            I confirm that I have added my{" "}
                            <span className="underline">
                              Polar webhook secret
                            </span>{" "}
                            in Convex.
                          </p>
                        ),
                        value: formValues.polar?.webhookSecret || "",
                      },
                      {
                        label: "Verify Webhook Fields",
                        type: "checkbox",
                        placeholder: "_",
                        onChange: (value) =>
                          handleFormChange(
                            "polar",
                            "verifyWebhookFields",
                            value
                          ),
                        checkboxLabel: (
                          <p className="text-white text-sm">
                            I confirm that I have selected{" "}
                            <span className="underline">all events</span> for my
                            Polar webhook.
                          </p>
                        ),
                        value: formValues.polar?.verifyWebhookFields || "",
                      },
                    ]
              }
              footer={
                <>
                  {supabasePaymentWebhookUrl ? (
                    <div className="mt-3 bg-[#1F1F22] p-3 rounded-md border border-[#2A2A2D]">
                      <p className="text-xs text-gray-300 mb-2">
                        Copy this URL for the Polar webhook:
                      </p>
                      <div
                        className="flex items-center justify-between bg-[#2A2A2D] p-2 rounded cursor-pointer hover:bg-[#333338]"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            supabasePaymentWebhookUrl
                          );
                          toast.success("Copied to clipboard");
                        }}
                      >
                        <span className="text-xs text-white font-mono">
                          {supabasePaymentWebhookUrl}
                        </span>
                        <Copy className="h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  ) : null}
                  <div
                    className={cn(
                      "mt-3 bg-[#1F1F22] p-3 rounded-md border border-[#2A2A2D]",
                      (!formValues.polar?.accessToken ||
                        !formValues.polar?.organizationId ||
                        noPaymentInputStack ||
                        (isSupabaseStack(selectedTemplateStack) &&
                          !supabaseConnected)) &&
                        "hidden"
                    )}
                  >
                    <div className="flex justify-between items-center">
                      <p
                        className={cn(
                          "text-xs text-gray-300",
                          numPolarProducts === 0 && "text-red-500"
                        )}
                      >
                        {numPolarProducts > 0 ? (
                          `You have ${numPolarProducts} Polar product(s) set up in this project.`
                        ) : (
                          <>
                            You have no Polar products set up in this project.
                            Please make sure your keys are from{" "}
                            <a
                              href="https://sandbox.polar.sh"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline"
                            >
                              sandbox.polar.sh
                            </a>{" "}
                            and have created a product and added it to your
                            project.
                          </>
                        )}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-6 px-2"
                        onClick={() => {
                          setPolarProductsRefetchTrigger((prev) => prev + 1);
                          toast.success("Refreshed Polar products");
                        }}
                        disabled={isLoadingPolarProducts}
                      >
                        {isLoadingPolarProducts ? "Loading..." : "Refresh"}
                      </Button>
                    </div>
                  </div>
                </>
              }
            />
          )}
        </div>

        <div className="flex justify-center flex-col items-center sticky bottom-6">
          {validationError && (
            <div className="text-red-500 mb-4">{validationError}</div>
          )}
          <Button
            variant="default"
            className={`rounded-lg text-white ${
              integrationComplete
                ? "bg-indigo-600 hover:bg-indigo-700"
                : "bg-gray-600 cursor-not-allowed opacity-50"
            }`}
            onClick={() => {
              if (integrationComplete) {
                toast.success("Integrations setup complete");
                // @ts-expect-error TS2345
                onComplete(selectedTemplateStack);
              } else {
                // If integration is not complete, go back
                onBack();
              }
            }}
            disabled={!integrationComplete}
          >
            Setup Integrations
          </Button>
        </div>
      </div>
    );
  };

  // Step 1 - Framework and stack selection
  const renderStep1 = () => {
    return (
      <div className="flex flex-col pb-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Framework</h1>
          <div className="flex flex-wrap gap-4">
            {frameworkOptions.map((option) => (
              <Card
                key={option.id}
                title={option.title}
                description={option.description}
                icon={option.icon}
                isSelected={option.isSelected}
                onClick={option.onClick}
                badge={option.badge}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Auth & Database</h1>
          <div className="flex flex-wrap gap-4">
            {authDatabaseOptions.map((option) => (
              <Card
                key={option.id}
                title={option.title}
                description={option.description}
                icon={option.icon}
                isSelected={option.isSelected}
                onClick={option.onClick}
                badge={option.badge}
                disabled={option.disabled}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-1 mb-4">
          <h1 className="text-2xl font-semibold">Payments</h1>
          <div className="flex flex-wrap gap-4">
            {paymentOptions.map((option) => (
              <Card
                key={option.id}
                title={option.title}
                description={option.description}
                icon={option.icon}
                isSelected={option.isSelected}
                onClick={option.onClick}
                badge={option.badge}
                disabled={option.disabled}
              />
            ))}
          </div>
        </div>

        <div className="flex justify-center flex-col items-center sticky bottom-6">
          <Button
            variant="default"
            className="bg-indigo-600 hover:bg-indigo-700 rounded-lg text-white"
            onClick={handleProceedToSetup}
          >
            Setup Integrations
          </Button>
        </div>
      </div>
    );
  };

  // Main render
  return (
    <div className="flex flex-col w-full h-full text-white mt-[2rem]">
      <div className="flex flex-col items-center justify-start w-full h-full px-6 py-6">
        <div className="max-w-5xl w-full overflow-y-auto">
          <div className="mb-8">
            <Button
              variant="outline"
              size="sm"
              style={{
                borderWidth: "1px",
                borderColor: "rgba(47, 48, 53, 1)",
                borderStyle: "solid",
              }}
              className="flex items-center rounded-lg gap-2"
              onClick={() => {
                if (currentStep === 2) {
                  setCurrentStep(1);
                } else {
                  onBack();
                }
              }}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
          {currentStep === 1 ? renderStep1() : renderStep2()}
        </div>
      </div>
    </div>
  );
};

export default ProviderSetup;
